import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { useAgbStore } from './agb.store';
import type { AgbStatsResponse, AgbTimeseriesResponse } from './agb.types';

/**
 * CTrees Global Aboveground Biomass 100m (2000–2025) — continuous, Mg/ha. JSON
 * stats and timeseries are served by the biomass API, while the self-templated per-year
 * tiles remain on the analytics infrastructure (Path A / GEE). The tile URL needs no
 * `/agb/layer` round-trip; `/agb/stats` returns a single-year carbon rollup, and
 * `/agb/timeseries` returns the whole 26-year trajectory in one call. The per-year
 * `/agb/tiles/{z}/{x}/{y}?year=YYYY` endpoints are JWT-protected (Lambda@Edge verifies
 * the bearer on every tile GET); `ApiService` attaches the token automatically on the
 * JSON calls, and the tile fetcher routes through the raster service so the bearer rides
 * on the tile requests too.
 *
 * Contract: ~/.claude/contract/260701_agb.md
 */
@Composable()
export class useAgbService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useAgbStore())
  public readonly agbStore!: ReturnType<typeof useAgbStore>;

  /**
   * Self-template the fixed, pre-coloured per-year tile URL. The contract guarantees this
   * path is stable, so we skip the `/agb/layer?year=N` round-trip entirely and hand the
   * `{z}/{x}/{y}` template straight to OL (auth rides via the raster tile fetcher).
   */
  public buildTileUrl(year: number): string {
    return `${import.meta.env.VITE_ANALYTICS_API}/agb/tiles/{z}/{x}/{y}?year=${year}`;
  }

  /** Single-year carbon rollup (Mg/ha mean + Mg / Mg C / Mg CO₂e stocks) for `year`. */
  public async getAgbStats(
    coords: MultiPolygonCoords,
    year: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AgbStatsResponse>> {
    return this.api.post<AgbStatsResponse>(
      `${import.meta.env.VITE_BIOMASS_API}/agb/stats?year=${year}`,
      { data: coords },
      { signal },
    );
  }

  /** 26-year carbon trajectory (dense, index-aligned arrays) — one call, no `year`. */
  public async getAgbTimeseries(
    coords: MultiPolygonCoords,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AgbTimeseriesResponse>> {
    return this.api.post<AgbTimeseriesResponse>(
      `${import.meta.env.VITE_BIOMASS_API}/agb/timeseries`,
      { data: coords },
      { signal },
    );
  }
}
